import React, { createContext, useContext } from 'react';
import { useIsChoreographedByAncestor } from '@atlassian/ipm-choreographer/ancestor-choreographer-context';
import type { ChoreographerContextProviderProps } from './types.tsx';

const choreographedStatusContextValue = {
	isAlreadyChoreographedAtAncestor: true,
};

export const ChoreographerContextProvider = ({
	isChoreographed,
	children,
}: ChoreographerContextProviderProps): React.JSX.Element => {
	const { isAlreadyChoreographedAtAncestor } = useChoreographedStatusContext();

	const shouldUseChoreographerContextProvider =
		!isAlreadyChoreographedAtAncestor && isChoreographed;

	return shouldUseChoreographerContextProvider ? (
		<ChoreographedStatusContext.Provider value={choreographedStatusContextValue}>
			{children}
		</ChoreographedStatusContext.Provider>
	) : (
		<>{children}</>
	);
};

export const ChoreographedStatusContext: React.Context<{
	isAlreadyChoreographedAtAncestor: boolean;
}> = createContext<{
	isAlreadyChoreographedAtAncestor: boolean;
}>({
	isAlreadyChoreographedAtAncestor: false,
});

export const useChoreographedStatusContext = (): {
	isAlreadyChoreographedAtAncestor: boolean;
} => {
	const { isAlreadyChoreographedAtAncestor } = useContext(ChoreographedStatusContext);
	// Also defer when an out-of-product ancestor (e.g. the Post Office message wrapper) is already
	// choreographing this subtree. Post Office choreographs through a separate engine, so without
	// this a Jira spotlight nested in the Post Office wrapper would double-choreograph a headless
	// message and collide.
	//
	// Gate tracing: the signal is published one hop away, by `PostOfficeMessage`
	// (`@atlassian/post-office-headless-api`), and only when its Statsig gate
	// `post-office_enable_choreographer-ancestor-context` is on. When that gate is off — or there is no Post
	// Office ancestor at all, which is the case for every existing Jira spotlight —
	// `useIsChoreographedByAncestor()` returns `false`, so this reduces to
	// `isAlreadyChoreographedAtAncestor || false` and existing Jira choreography is preserved exactly.
	const isChoreographedByPostOfficeAncestor = useIsChoreographedByAncestor();

	return {
		isAlreadyChoreographedAtAncestor:
			isAlreadyChoreographedAtAncestor || isChoreographedByPostOfficeAncestor,
	};
};
